import React, { useCallback, useMemo, useRef, useState } from 'react';
import { X, Upload as UploadIcon, Download, Check } from 'lucide-react';
import MapStep from './MapStep';
import ReviewStep from './ReviewStep';
import {
  parseFile,
  autoMatchColumns,
  validateRows,
  downloadTemplate,
  type ParsedFile,
  type ValidatedRow,
} from '../../../../../hooks/useImportChartOfAccounts';

type Step = 'upload' | 'map' | 'review' | 'done';

interface ImportChartOfAccountsModalProps {
  open: boolean;
  onClose: () => void;
  existingAccountNames: string[];
  onImport: (rows: ValidatedRow[]) => void;
}

export default function ImportChartOfAccountsModal({ open, onClose, existingAccountNames, onImport }: ImportChartOfAccountsModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mapping, setMapping] = useState<string[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState({ imported: 0, duplicates: 0, errors: 0 });

  const resetAll = useCallback(() => {
    setStep('upload');
    setParsed(null);
    setMapping([]);
    setImportError(null);
    setImportedCount({ imported: 0, duplicates: 0, errors: 0 });
  }, []);

  const handleClose = useCallback(() => { resetAll(); onClose(); }, [resetAll, onClose]);

  const handleFile = useCallback(async (file: File) => {
    setImportError(null);
    try {
      const result = await parseFile(file);
      setParsed(result);
      setMapping(autoMatchColumns(result.headers));
      setStep('map');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Could not read the file.');
    }
  }, []);

  const validated = useMemo<ValidatedRow[]>(() => {
    if (!parsed || mapping.length === 0) return [];
    return validateRows(parsed, mapping, existingAccountNames);
  }, [parsed, mapping, existingAccountNames]);

  const canContinueFromMap = mapping.includes('name') && mapping.includes('accountType');

  const handleConfirmImport = useCallback(() => {
    const ready = validated.filter((r) => r.status === 'ready');
    const dup = validated.filter((r) => r.status === 'duplicate').length;
    const err = validated.filter((r) => r.status === 'error').length;
    onImport(ready);
    setImportedCount({ imported: ready.length, duplicates: dup, errors: err });
    setStep('done');
  }, [validated, onImport]);

  if (!open) return null;

  const title = step === 'upload' ? 'Import chart of accounts' : step === 'map' ? 'Map your columns' : 'Review and import';
  const sub = step === 'upload'
    ? 'Upload a CSV file to add accounts in bulk.'
    : step === 'map'
      ? `Match each column from your file to the matching Novala field. Novala auto-matched ${mapping.filter((m) => m !== 'ignore').length} of ${mapping.length} columns.`
      : `Novala parsed ${validated.length} rows.`;

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(14,26,26,0.3)' }} onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white border border-line rounded-2xl flex flex-col pointer-events-auto"
          style={{
            boxShadow: '0 24px 60px rgba(14,26,26,0.14)',
            width: '100%',
            maxWidth: step === 'map' || step === 'review' ? 860 : 560,
            maxHeight: 'calc(100vh - 32px)',
          }}
        >
          {step !== 'done' ? (
            <>
              <div className="px-7 py-5 border-b border-line flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="m-0 text-xl font-extrabold text-ink" style={{ letterSpacing: '-0.3px' }}>{title}</h2>
                  <div className="text-[12.5px] text-ink3 mt-1 leading-snug">{sub}</div>
                </div>
                <button type="button" onClick={handleClose} aria-label="Close" className="w-9 h-9 rounded-md text-ink3 hover:bg-panel hover:text-ink flex items-center justify-center bg-transparent border border-transparent hover:border-line cursor-pointer flex-shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <Stepper step={step} />

              <div className="flex-1 overflow-y-auto px-7 py-5">
                {step === 'upload' && <UploadStep onFile={handleFile} error={importError} />}
                {step === 'map' && parsed && (
                  <MapStep
                    parsed={parsed}
                    mapping={mapping}
                    onMappingChange={(idx, value) => setMapping((prev) => { const next = [...prev]; next[idx] = value; return next; })}
                    onReplace={() => setStep('upload')}
                  />
                )}
                {step === 'review' && <ReviewStep rows={validated} />}
              </div>

              <div className="px-6 py-3.5 border-t border-line bg-[#FDFDFD] flex justify-between items-center gap-3">
                {step === 'upload' ? (
                  <>
                    <span></span>
                    <button type="button" onClick={handleClose} className="text-[13px] font-semibold text-ink3 bg-transparent border-none cursor-pointer hover:text-ink">Cancel</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => setStep(step === 'review' ? 'map' : 'upload')} className="h-9 px-4 rounded-lg bg-white text-ink2 text-[13px] font-semibold border border-line cursor-pointer hover:bg-panel">
                      Back
                    </button>
                    <div className="flex gap-3 items-center">
                      <button type="button" onClick={handleClose} className="text-[13px] font-semibold text-ink3 bg-transparent border-none cursor-pointer hover:text-ink">Cancel</button>
                      {step === 'map' && (
                        <button type="button" onClick={() => setStep('review')} disabled={!canContinueFromMap}
                          className="h-9 px-4 rounded-lg text-white text-[13px] font-semibold border-none"
                          style={{ background: canContinueFromMap ? '#0E1A1A' : '#96A0AB', cursor: canContinueFromMap ? 'pointer' : 'not-allowed' }}>
                          Continue to review
                        </button>
                      )}
                      {step === 'review' && (
                        <button type="button" onClick={handleConfirmImport}
                          className="h-9 px-4 rounded-lg text-white text-[13px] font-semibold border-none cursor-pointer"
                          style={{ background: '#0E1A1A' }}>
                          Import {validated.filter((r) => r.status === 'ready').length} accounts
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <DoneStep count={importedCount} onImportAnother={resetAll} onDone={handleClose} />
          )}
        </div>
      </div>
    </>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'upload', label: 'Upload file' },
    { key: 'map', label: 'Map columns' },
    { key: 'review', label: 'Review and import' },
  ];
  const order: Step[] = ['upload', 'map', 'review'];
  const currentIdx = order.indexOf(step);
  return (
    <div className="flex items-center gap-1.5 px-7 py-3 border-b border-lineSoft bg-[#FDFDFD]">
      {steps.map((s, idx) => {
        const isActive = s.key === step;
        const isDone = idx < currentIdx;
        return (
          <React.Fragment key={s.key}>
            <div className={`inline-flex items-center gap-1.5 text-[12.5px] ${isActive ? 'text-ink font-bold' : isDone ? 'font-semibold' : 'text-ink3 font-medium'}`} style={{ color: isDone ? '#0F6E56' : undefined }}>
              <span className="w-[22px] h-[22px] rounded-full inline-flex items-center justify-center text-[11px] font-bold" style={{ background: isActive ? '#0E1A1A' : isDone ? '#0F6E56' : '#D8DEE6', color: isActive || isDone ? '#fff' : '#5A6B6B' }}>
                {isDone ? '✓' : idx + 1}
              </span>
              {s.label}
            </div>
            {idx < steps.length - 1 && <div className="h-px bg-line w-8 mx-0.5"></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function UploadStep({ onFile, error }: { onFile: (file: File) => void; error: string | null }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };
  return (
    <div>
      <div
        className="rounded-xl py-12 px-6 text-center cursor-pointer transition-all"
        style={{ border: `2px dashed ${dragOver ? '#0F6E56' : '#D8DEE6'}`, background: dragOver ? '#E6F2EE' : '#F7F9FB' }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="w-14 h-14 rounded-xl bg-white border border-line inline-flex items-center justify-center text-ink mb-3.5">
          <UploadIcon className="w-6 h-6" />
        </div>
        <div className="text-[15px] font-bold text-ink">Drop a CSV file here</div>
        <div className="text-[12.5px] text-ink3 mt-1">or <span className="font-bold" style={{ color: '#0F6E56' }}>browse to upload</span></div>
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={(e) => { const file = e.target.files?.[0]; if (file) onFile(file); e.target.value = ''; }} />
      </div>

      {error && (
        <div className="mt-3 text-[12.5px] font-medium px-4 py-2.5 rounded-lg" style={{ background: '#FCE8EE', color: '#8B1538', border: '1px solid #F0B0C0' }}>
          {error}
        </div>
      )}

      <div className="mt-4 px-4 py-3.5 rounded-lg text-[12.5px] text-ink3 border border-lineSoft bg-[#FAFBFC] leading-relaxed">
        <strong className="text-ink2">Accepted:</strong> .csv &nbsp;·&nbsp; <strong className="text-ink2">Max size:</strong> 5 MB &nbsp;·&nbsp; <strong className="text-ink2">Max rows:</strong> 1,000
        <br />
        First row should be column headers. Required fields: Account name, Account type.
        <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1 font-semibold cursor-pointer bg-transparent border-none p-0 mt-2.5" style={{ color: '#0F6E56' }}>
          <Download className="w-3.5 h-3.5" />
          Download the Novala template (.csv)
        </button>
      </div>
    </div>
  );
}

function DoneStep({ count, onImportAnother, onDone }: { count: { imported: number; duplicates: number; errors: number }; onImportAnother: () => void; onDone: () => void }) {
  const skipped = count.duplicates + count.errors;
  const skippedText = skipped > 0
    ? count.duplicates > 0 && count.errors > 0
      ? ` (${count.duplicates} duplicate, ${count.errors} errors)`
      : count.duplicates > 0 ? ` (${count.duplicates} duplicate)` : ` (${count.errors} errors)`
    : '';
  return (
    <div className="py-12 px-6 text-center">
      <div className="w-[72px] h-[72px] rounded-full inline-flex items-center justify-center mb-4" style={{ background: '#E6F2EE', color: '#0F6E56' }}>
        <Check className="w-8 h-8" strokeWidth={2.5} />
      </div>
      <h3 className="m-0 text-xl font-extrabold text-ink" style={{ letterSpacing: '-0.3px' }}>Import complete</h3>
      <div className="text-[32px] font-extrabold mt-2.5 mb-0.5" style={{ color: '#0F6E56' }}>{count.imported}</div>
      <p className="text-[13px] text-ink3 m-0">
        accounts added to your Chart of accounts.
        {skipped > 0 && (<><br />{skipped} rows were skipped{skippedText}.</>)}
      </p>
      <div className="mt-6 flex gap-2.5 justify-center">
        <button type="button" onClick={onImportAnother} className="h-9 px-4 rounded-lg bg-white text-ink2 text-[13px] font-semibold border border-line cursor-pointer hover:bg-panel">
          Import another file
        </button>
        <button type="button" onClick={onDone} className="h-9 px-5 rounded-lg text-white text-[13px] font-semibold border-none cursor-pointer" style={{ background: '#0E1A1A' }}>
          Done
        </button>
      </div>
    </div>
  );
}